import LacunaWebPKI from "web-pki";
import config from "../Config/config.json";

// Lê o comprimento (length) DER a partir da posição `p`.
// Suporta short form e long form. Retorna { len, next }.
function readDerLength(bytes, p) {
  let len = bytes[p];
  p += 1;
  if (len & 0x80) {
    const numBytes = len & 0x7f;
    len = 0;
    for (let k = 0; k < numBytes && p < bytes.length; k += 1) {
      len = (len << 8) | bytes[p];
      p += 1;
    }
  }
  return { len, next: p };
}

// Extrai o RUC do Subject Alternative Name (Endereço do Diretório) de um
// certificado paraguaio. O RUC fica no atributo serialNumber (OID 2.5.4.5)
// dentro do directoryName do SAN (OID 2.5.29.17) — que o Web-PKI/RestPKI não
// expõem. A busca é restrita ao conteúdo do SAN para não capturar o RUC da
// autoridade certificadora (que aparece no campo do emissor).
export function extractRucFromDer(base64Der) {
  try {
    const bin = atob(base64Der);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) {
      bytes[i] = bin.charCodeAt(i);
    }

    // OID da extensão SAN: 2.5.29.17 => 06 03 55 1D 11
    const sanOid = [0x06, 0x03, 0x55, 0x1d, 0x11];
    let sanStart = -1;
    for (let i = 0; i + sanOid.length <= bytes.length; i += 1) {
      let ok = true;
      for (let j = 0; j < sanOid.length; j += 1) {
        if (bytes[i + j] !== sanOid[j]) {
          ok = false;
          break;
        }
      }
      if (ok) {
        sanStart = i + sanOid.length;
        break;
      }
    }
    if (sanStart === -1) return null;

    // Após o OID pode vir um BOOLEAN (critical) e depois o OCTET STRING (extnValue).
    let p = sanStart;
    if (bytes[p] === 0x01) {
      p += 1;
      const boolLen = readDerLength(bytes, p);
      p = boolLen.next + boolLen.len;
    }
    if (bytes[p] !== 0x04) return null; // esperado OCTET STRING
    p += 1;
    const octet = readDerLength(bytes, p);
    const sanEnd = octet.next + octet.len;

    // Dentro do SAN, procura o serialNumber (2.5.4.5 => 06 03 55 04 05).
    const snOid = [0x06, 0x03, 0x55, 0x04, 0x05];
    const stringTags = [0x13, 0x0c, 0x14, 0x16, 0x12]; // Printable/UTF8/T61/IA5/Numeric
    const values = [];
    for (let i = octet.next; i + snOid.length <= sanEnd; i += 1) {
      let ok = true;
      for (let j = 0; j < snOid.length; j += 1) {
        if (bytes[i + j] !== snOid[j]) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      let q = i + snOid.length;
      const tag = bytes[q];
      q += 1;
      if (!stringTags.includes(tag)) continue;
      const value = readDerLength(bytes, q);
      let str = "";
      for (let k = 0; k < value.len; k += 1) {
        str += String.fromCharCode(bytes[value.next + k]);
      }
      values.push(str.trim());
    }

    // Prefere o valor que começa com "RUC"; senão, o primeiro serialNumber do SAN.
    return values.find((v) => /^RUC/i.test(v)) || values[0] || null;
  } catch (e) {
    console.error("Erro ao extrair RUC do certificado:", e);
    return null;
  }
}

export default class WebPkiService {
  constructor() {
    this._webpki = new LacunaWebPKI();
  }

  async init() {
    return new Promise((resolve) => {
      this._webpki.init({
        restPkiUrl: config.RESTPKI_ENDPOINT,
        ready: resolve,
      });
    });
  }

  async loadCertificates(pais) {
    return new Promise((resolve, reject) => {
      // Define o filtro baseado no país
      let filter;
      if (pais === "BR") {
        // Brasil: apenas certificados com CPF
        filter = this._webpki.filters.all(
          this._webpki.filters.isWithinValidity,
          this._webpki.filters.hasPkiBrazilCpf
        );
      } else {
        // Paraguai: apenas certificados SEM CPF brasileiro
        filter = this._webpki.filters.all(
          this._webpki.filters.isWithinValidity,
          function (cert) {
            // Aceita certificados que não tem pkiBrazil ou que o cpf é vazio/null
            return !cert.pkiBrazil || !cert.pkiBrazil.cpf;
          }
        );
      }

      this._webpki
        .listCertificates({
          selectId: "certificateSelect",
          filter: filter,
          // Function that will be called to get the text that should be displayed for each option:
          selectOptionFormatter: function (cert) {
            var s = cert.subjectName + " (Emitido por " + cert.issuerName + ")";
            if (new Date() > cert.validityEnd) {
              s = "[Vencido] " + s;
            }
            return s;
          },
        })
        .success(resolve)
        .fail(reject);
    });
  }

  async signWithRestPki(thumbprint, token) {
    return new Promise((resolve, reject) =>
      this._webpki
        .signWithRestPki({ token, thumbprint })
        .success(resolve)
        .fail(reject)
    );
  }

  async readCertificate(thumbprint) {
    return new Promise((resolve, reject) =>
      this._webpki.readCertificate(thumbprint).success(resolve).fail(reject)
    );
  }

  // Lê o certificado (DER em Base64) e extrai o RUC do Subject Alternative Name.
  async readParaguayRuc(thumbprint) {
    const der = await this.readCertificate(thumbprint);
    return extractRucFromDer(der);
  }

  async signHash(thumbprint, toSignHash, digestAlgorithmOid) {
    return new Promise((resolve, reject) =>
      this._webpki
        .signHash({
          thumbprint,
          hash: toSignHash,
          digestAlgorithm: digestAlgorithmOid,
        })
        .success(resolve)
        .fail(reject)
    );
  }
}
